// src/features/messages/components/ReactionBar.jsx
import React, { useState } from 'react';
import { Box, Chip, IconButton, Popover, Tooltip } from '@mui/material';
import { AddReactionOutlined as AddReactionIcon } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { REACTIONS, REACTION_EMOJI } from '../constants';

/**
 * The reactions on a message as chips (click to toggle your own), and a
 * button to pick a new one. `alwaysShowPicker` lays all reactions out as
 * buttons instead, for when reacting is the main thing to do.
 */
export default function ReactionBar({ reactions = [], onToggle, disabled = false, alwaysShowPicker = false }) {
  const { t } = useTranslation(['messages']);
  const [anchorEl, setAnchorEl] = useState(null);
  const byKey = Object.fromEntries(reactions.map(r => [r.reaction, r]));

  const tooltip = (reaction) => {
    const names = reaction.staff.map(s => s.name).join(', ');
    return `${t(`messages:reactions.${reaction.reaction}`)}: ${names}`;
  };

  const pick = (key) => {
    setAnchorEl(null);
    onToggle(key);
  };

  if (alwaysShowPicker) {
    return (
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
        {REACTIONS.map(({ key, emoji }) => {
          const reaction = byKey[key];
          return (
            <Tooltip key={key} title={reaction ? tooltip(reaction) : t(`messages:reactions.${key}`)}>
              <Chip
                label={reaction ? `${emoji} ${reaction.count}` : emoji}
                onClick={() => onToggle(key)}
                disabled={disabled}
                color={reaction?.reacted_by_me ? 'primary' : 'default'}
                variant={reaction?.reacted_by_me ? 'filled' : 'outlined'}
                sx={{ fontSize: '0.95rem', height: 28, minWidth: 40 }}
                aria-pressed={Boolean(reaction?.reacted_by_me)}
              />
            </Tooltip>
          );
        })}
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 0.75 }}>
      {reactions.map(reaction => (
        <Tooltip key={reaction.reaction} title={tooltip(reaction)}>
          <Chip
            size="small"
            label={`${REACTION_EMOJI[reaction.reaction] ?? reaction.reaction} ${reaction.count}`}
            onClick={() => onToggle(reaction.reaction)}
            disabled={disabled}
            color={reaction.reacted_by_me ? 'primary' : 'default'}
            variant={reaction.reacted_by_me ? 'filled' : 'outlined'}
            sx={{ fontSize: '0.875rem' }}
            aria-pressed={reaction.reacted_by_me}
          />
        </Tooltip>
      ))}
      <Tooltip title={t('messages:reactions.add')}>
        <span>
          <IconButton size="small" onClick={(e) => setAnchorEl(e.currentTarget)} disabled={disabled} aria-label={t('messages:reactions.add')}>
            <AddReactionIcon fontSize="small" />
          </IconButton>
        </span>
      </Tooltip>
      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        transformOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Box sx={{ display: 'flex', p: 0.5 }}>
          {REACTIONS.map(({ key, emoji }) => (
            <Tooltip key={key} title={t(`messages:reactions.${key}`)}>
              <IconButton
                onClick={() => pick(key)}
                sx={{ fontSize: '1.4rem', width: 44, height: 44, ...(byKey[key]?.reacted_by_me && { bgcolor: 'action.selected' }) }}
                aria-label={t(`messages:reactions.${key}`)}
              >
                {emoji}
              </IconButton>
            </Tooltip>
          ))}
        </Box>
      </Popover>
    </Box>
  );
}
